import { legacyEmployeeRouteMap } from '../../src/navigation/employeeLinks';
import { PERMISSION_RESOURCE_CODES as R } from '../../src/permissions/definitions';
import type { AppRoute } from '../routes';

const boundary = '@/modules/zy-hr/EmployeeDomainBoundary';
const page = (
  path: string,
  name: string,
  resource: string,
  component: string,
  icon?: string,
): AppRoute => ({
  path,
  name,
  icon,
  permissionResourceCode: resource,
  component: `@/modules/zy-hr/${component}`,
});
const detail = (
  path: string,
  resource: string,
  component: string,
  parent: string,
): AppRoute => ({
  ...page(path, '', resource, `pages/${component}`),
  hideInMenu: true,
  parentKeys: ['/employee-center', parent],
});
const lifecycle = (
  path: string,
  name: string,
  resource: string,
  list: string,
  form: string,
  icon: string,
): AppRoute[] => [
  page(path, name, resource, `pages/${list}`, icon),
  detail(`${path}/new`, resource, form, path),
  detail(`${path}/:uuid/edit`, resource, form, path),
  detail(`${path}/:uuid/view`, resource, form, path),
];

export const employeeRoutes: AppRoute[] = [
  {
    path: '/employee-center',
    name: '员工中心',
    icon: 'team',
    wrappers: [boundary],
    routes: [
      {
        path: '/employee-center/',
        hideInMenu: true,
        component: './Platform/OverviewRedirect',
      },
      page(
        '/employee-center/overview',
        '概览',
        R.workbench,
        'pages/EmployeeWorkbench',
        'home',
      ),
      page(
        '/employee-center/approval',
        '我的审批',
        R.approval,
        'pages/ApprovalCenter',
        'audit',
      ),
      page(
        '/employee-center/employees',
        '员工花名册',
        R.employees,
        'pages/EmployeeList',
        'contacts',
      ),
      detail(
        '/employee-center/employees/new',
        R.employees,
        'EmployeeNew',
        '/employee-center/employees',
      ),
      detail(
        '/employee-center/employees/recycle-bin',
        R.employees,
        'EmployeeRecycleBin',
        '/employee-center/employees',
      ),
      detail(
        '/employee-center/employees/:uuid',
        R.employees,
        'EmployeeDetail',
        '/employee-center/employees',
      ),
      ...lifecycle(
        '/employee-center/lifecycle/employee-change',
        '员工异动',
        R.employeeChange,
        'EmployeeChangeList',
        'employee-change-form',
        'branches',
      ),
      ...lifecycle(
        '/employee-center/lifecycle/onboarding',
        '待入职',
        R.onboarding,
        'OnboardingList',
        'onboarding-form',
        'userAdd',
      ),
      ...lifecycle(
        '/employee-center/lifecycle/regularization',
        '转正',
        R.regularization,
        'RegularizationList',
        'regularization-form',
        'fileDone',
      ),
      ...lifecycle(
        '/employee-center/lifecycle/resignation',
        '离职',
        R.resignation,
        'ResignationList',
        'resignation-form',
        'logout',
      ),
      {
        path: '/employee-center/relations',
        name: '员工关系',
        icon: 'safetyCertificate',
        hideInMenu: true,
        routes: [
          page(
            '/employee-center/relations/labor-relations',
            '劳动关系',
            R.laborRelations,
            'employee-center/LaborRelationsPage',
            'safetyCertificate',
          ),
          page(
            '/employee-center/relations/certificate-requests',
            '证明申请处理',
            R.certificateRequests,
            'employee-center/CertificateRequestsPage',
            'fileDone',
          ),
          page(
            '/employee-center/relations/analysis',
            '员工关系分析',
            R.relationsAnalysis,
            'employee-center/EmployeeRelationsAnalysisPage',
            'areaChart',
          ),
        ],
      },
      page(
        '/employee-center/lifecycle-audits',
        '员工操作记录',
        R.lifecycleAudits,
        'pages/EmployeeLifecycleAuditList',
        'history',
      ),
    ],
  },
];

export const employeeSettingRoutes: AppRoute[] = [
  {
    path: '/settings/employee',
    name: '员工中心设置',
    icon: 'team',
    wrappers: [boundary],
    routes: [
      {
        path: '/settings/employee/',
        hideInMenu: true,
        component: './Platform/OverviewRedirect',
      },
      page(
        '/settings/employee/position-settings',
        '岗位配置',
        R.positionSettings,
        'employee-center/PositionSettingsPage',
        'apartment',
      ),
      page(
        '/settings/employee/job-level-settings',
        '职级管理',
        R.jobLevelSettings,
        'employee-center/JobLevelSettingsPage',
        'bars',
      ),
      page(
        '/settings/employee/work-location-settings',
        '工作地点',
        R.workLocationSettings,
        'employee-center/WorkLocationSettingsPage',
        'environment',
      ),
      page(
        '/settings/employee/handover-templates',
        '离职交接模板',
        R.handoverTemplates,
        'employee-center/HandoverTemplatesPage',
        'solution',
      ),
      page(
        '/settings/employee/employee-reminder-settings',
        '员工提醒设置',
        R.reminderSettings,
        'employee-center/EmployeeReminderSettingsPage',
        'bell',
      ),
      page(
        '/settings/employee/workflow-integration-settings',
        '审批流程设置',
        R.workflowSettings,
        'employee-center/WorkflowIntegrationSettingsPage',
        'branches',
      ),
    ],
  },
];

export const legacyEmployeeRoutes: AppRoute[] = Object.keys(
  legacyEmployeeRouteMap,
)
  .flatMap((path) => [path, `/zy-hr${path}`])
  .concat(['/zy-hr', '/zy-hr/employee-center'])
  .map((path) => ({
    path,
    hideInMenu: true,
    component: './Platform/LegacyEmployeeRedirect',
  }));
